<template>
  <section v-if="isAdmin" class="real-positions mb-3 p-3" aria-label="当前实盘持仓">
    <button class="holdings-toggle" :aria-expanded="panelExpanded" aria-controls="real-holdings-content" @click="panelExpanded = !panelExpanded">
      <b-icon :icon="panelExpanded ? 'chevron-down' : 'chevron-right'" class="mr-2" />
      <strong>当前实盘持仓（{{ stockGroups.length }}只股票）</strong>
      <span class="small ml-2">{{ panelExpanded ? '收起' : '展开' }}</span>
    </button>
    <b-collapse id="real-holdings-content" v-model="panelExpanded">
    <div class="d-flex align-items-center flex-wrap mt-2 mb-2">
      <b-form-select v-model="group" :options="groups" size="sm" class="group-select mr-2" :disabled="busy || confirming" />
      <b-button size="sm" variant="outline-info" :disabled="loading || busy || confirming" @click="load">
        {{ loading ? '查询中…' : '刷新实盘持仓' }}
      </b-button>
      <b-button size="sm" variant="danger" class="ml-2" :disabled="!selected.length || busy || loading || consumed" @click="confirmSell(selected)">
        卖出所选（{{ selectedStockCount }}只 / {{ selected.length }}账户）
      </b-button>
      <small class="ml-auto pt-1">{{ updatedAt ? `查询时间 ${updatedAt}` : '' }}</small>
    </div>
    <p class="small mb-2">按股票汇总当前实盘持仓。选择股票会勾选其全部可卖账户，展开明细可取消个别账户；不可卖账户不参与委托。</p>
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
    <b-alert v-for="message in errors" :key="message" show variant="warning" class="py-1">{{ message }}；该部分持仓可能未显示。</b-alert>
    <div class="stock-groups">
      <article v-for="stock in stockGroups" :key="stock.code" class="stock-group mb-2">
        <div class="stock-header p-2">
          <b-form-checkbox :checked="stockSelected(stock)" :indeterminate="stockPartial(stock)" :disabled="locked || !stock.sellable.length" :aria-label="`选择${stock.name}全部可卖账户`" @change="selectStock(stock, $event)" />
          <button class="stock-title" :aria-expanded="!!expanded[stock.code]" @click="toggleStock(stock.code)">
            <b-icon :icon="expanded[stock.code] ? 'chevron-down' : 'chevron-right'" class="mr-1" />
            <strong>{{ stock.name }}</strong><span class="ml-2 small">{{ stock.code }}</span>
          </button>
          <span class="stock-summary small">{{ stock.rows.length }}个账户 · 持有 {{ stock.quantity }}股 · 可卖 {{ stock.available }}股</span>
          <label :for="`stock-price-${stock.code}`" class="small mb-0">统一限价</label>
          <b-form-input :id="`stock-price-${stock.code}`" :value="stockPrices[stock.code]" type="number" min="0.001" step="0.001" size="sm" :disabled="locked" class="price-input" @input="setStockPrice(stock, $event)" />
          <b-button size="sm" variant="danger" :disabled="locked || !stock.sellable.length" @click="sellStock(stock)">卖出这只股票</b-button>
          <b-button size="sm" variant="link" @click="toggleStock(stock.code)">{{ expanded[stock.code] ? '收起账户' : '账户明细' }}</b-button>
        </div>
        <div v-if="expanded[stock.code]" class="table-responsive px-2">
          <table class="table table-sm mb-1 account-table">
            <thead><tr><th>选择</th><th>实盘组 / 节点</th><th>账户</th><th>持有</th><th>可卖</th><th>成本 / 现价</th><th>持仓盈亏（元）</th><th>盈亏比例</th><th>委托价格</th><th>状态</th></tr></thead>
            <tbody><tr v-for="row in stock.rows" :key="row.id">
              <td><b-form-checkbox v-model="selected" :value="row.id" :disabled="!canSell(row) || locked" :aria-label="`选择${stock.name}账户${accountLabel(row)}`" /></td>
              <td>{{ row.group.toUpperCase() }} / {{ row.node_name }}</td><td>{{ accountLabel(row) }}</td>
              <td>{{ row.quantity }}</td><td>{{ row.available }}</td><td>{{ formatPrice(row.cost_price) }} / {{ formatPrice(row.price) }}</td>
              <td :class="pnlClass(row.pnl_amount)">{{ formatPnl(row.pnl_amount) }}</td>
              <td :class="pnlClass(row.pnl_percent)">{{ formatPnl(row.pnl_percent, '%') }}</td>
              <td><b-form-input v-model.number="row.orderPrice" type="number" min="0.001" step="0.001" size="sm" :disabled="locked" :aria-label="`${stock.name}账户${accountLabel(row)}委托价格`" class="price-input" /></td>
              <td>{{ !row.sell_enabled ? '账户禁止卖出' : row.available > 0 ? '可卖' : '无可卖数量' }}</td>
            </tr></tbody>
          </table>
        </div>
      </article>
      <p v-if="!stockGroups.length" class="small py-2 mb-0">{{ loading ? '正在向交易端查询…' : '本组暂无已查询到的持仓' }}</p>
    </div>
    <div v-if="results.length" class="mt-2" role="status">
      <strong>本次委托结果</strong>
      <div v-for="item in results" :key="item.id" class="small py-1">
        {{ item.node_name }} / {{ accountLabel(item) }} / {{ item.code }}：
        <span :class="item.status === 'submitted' ? 'text-success' : 'text-danger'">{{ item.status === 'submitted' ? '已提交（待核对成交）' : item.status === 'unknown' ? '结果未知' : '未成功' }}</span>
        {{ item.message }}
      </div>
    </div>
    </b-collapse>
    <b-modal v-model="confirming" title="确认实盘卖出" hide-footer :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy">
      <p>将向以下账户提交卖出委托：</p>
      <b-form-select v-model.number="percent" :options="[{ value: 1, text: '卖出全部可用股数' }, { value: 0.5, text: '卖出可用股数的50%（向下取整至100股）' }]" class="mb-2" :disabled="busy" />
      <div v-for="stock in confirmationStocks" :key="stock.code" class="mb-3">
        <strong>{{ stock.name }}（{{ stock.code }}）</strong>
        <div v-for="row in stock.rows" :key="row.id" class="small my-2">
          <b-form-checkbox v-model="pendingIds" :value="row.id" :disabled="busy || consumed || !canSell(row)">
            {{ row.group.toUpperCase() }} / {{ row.node_name }} / {{ accountLabel(row) }}
            <span v-if="!canSell(row)"> · {{ row.sell_enabled ? '无可卖数量' : '账户禁止卖出' }}</span>
            <span v-else> · {{ sellQuantity(row) }}股 · 限价 {{ formatPrice(row.orderPrice) }}元</span>
          </b-form-checkbox>
        </div>
      </div>
      <b-alert v-if="submitError" show variant="danger">{{ submitError }}</b-alert>
      <p class="small">结果未知时请先核对交易端委托，避免重复卖出。提交后请刷新实际持仓。</p>
      <div class="text-right">
        <b-button variant="secondary" :disabled="busy" @click="confirming = false">取消</b-button>
        <b-button variant="danger" class="ml-2" :disabled="busy || consumed || !pendingRows.length || pendingRows.some(row => sellQuantity(row) <= 0)" @click="submit">{{ busy ? '提交中…' : '确认提交实盘卖出' }}</b-button>
      </div>
    </b-modal>
  </section>
</template>

<script>
import { request_zzquant } from 'network/request.js'

export default {
  name: 'RealPositionControl',
  data() {
    return {
      isAdmin: false, panelExpanded: false, rows: [], errors: [], error: '', updatedAt: '', snapshot: '',
      selected: [], expanded: {}, stockPrices: {}, confirmationCodes: [], group: 'all', loading: false, busy: false, confirming: false,
      consumed: false, percent: 1, pendingIds: [], results: [], submitError: '',
      groups: [{ value: 'all', text: '全部实盘组' }, { value: 'v2', text: 'V2实盘组' }, { value: 'v3', text: 'V3实盘组' }]
    }
  },
  computed: {
    visibleRows() { return this.rows.filter(row => this.group === 'all' || row.group === this.group) },
    pendingRows() { return this.rows.filter(row => this.pendingIds.includes(row.id)) },
    locked() { return this.busy || this.confirming || this.consumed || this.loading },
    selectedStockCount() { return new Set(this.rows.filter(row => this.selected.includes(row.id)).map(row => row.code)).size },
    stockGroups() { return this.groupRows(this.visibleRows) },
    confirmationStocks() { return this.groupRows(this.visibleRows.filter(row => this.confirmationCodes.includes(row.code))) }
  },
  watch: { group() { this.selected = [] } },
  mounted() {
    try {
      const roles = JSON.parse(localStorage.getItem('user_info') || '{}').roles || []
      this.isAdmin = (Array.isArray(roles) ? roles : String(roles).split(',')).some(role => String(role).trim().toLowerCase() === 'admin')
    } catch (e) { this.isAdmin = false }
    if (this.isAdmin) this.load()
  },
  methods: {
    accountLabel(row) {
      const name = row.account_name || '未命名账户'
      return `${name}（${String(row.account_id || '').slice(-4)}）`
    },
    canSell(row) { return row.sell_enabled && row.available > 0 },
    sellQuantity(row) { return this.percent === 1 ? row.available : Math.floor(row.available * this.percent / 100) * 100 },
    groupRows(rows) {
      const stocks = new Map()
      rows.forEach(row => {
        if (!stocks.has(row.code)) stocks.set(row.code, { code: row.code, name: row.name || row.code, rows: [], quantity: 0, available: 0, sellable: [] })
        const stock = stocks.get(row.code)
        if (row.name && row.name !== row.code) stock.name = row.name
        stock.rows.push(row)
        stock.quantity += Number(row.quantity) || 0
        stock.available += Number(row.available) || 0
        if (this.canSell(row)) stock.sellable.push(row)
      })
      return Array.from(stocks.values()).sort((a, b) => a.code.localeCompare(b.code))
    },
    formatPrice(value) {
      const price = Number(value)
      return Number.isFinite(price) && price > 0 ? price.toFixed(3).replace(/0$/, '') : '—'
    },
    formatPnl(value, suffix = '') {
      if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—'
      const amount = Number(value)
      return `${amount > 0 ? '+' : ''}${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${suffix}`
    },
    pnlClass(value) { return value > 0 ? 'text-danger' : value < 0 ? 'text-success' : '' },
    toggleStock(code) { this.$set(this.expanded, code, !this.expanded[code]) },
    stockSelected(stock) { return stock.sellable.length > 0 && stock.sellable.every(row => this.selected.includes(row.id)) },
    stockPartial(stock) { return !this.stockSelected(stock) && stock.sellable.some(row => this.selected.includes(row.id)) },
    selectStock(stock, checked) {
      const ids = new Set(stock.rows.map(row => row.id))
      this.selected = this.selected.filter(id => !ids.has(id))
      if (checked) {
        this.selected.push(...stock.sellable.map(row => row.id))
        this.$set(this.expanded, stock.code, true)
      }
    },
    setStockPrice(stock, value) {
      this.$set(this.stockPrices, stock.code, value)
      stock.rows.forEach(row => { row.orderPrice = value === '' ? null : Number(value) })
    },
    sellStock(stock) {
      const chosen = stock.sellable.filter(row => this.selected.includes(row.id))
      if (!chosen.length) this.selectStock(stock, true)
      this.confirmSell((chosen.length ? chosen : stock.sellable).map(row => row.id))
    },
    async load() {
      if (this.busy || this.confirming || !this.isAdmin) return
      this.loading = true
      this.error = ''
      this.selected = []
      this.rows = []
      this.snapshot = ''
      try {
        const res = await request_zzquant({ url: '/v3/quant/real/positions', method: 'get', timeout: 25000, noRedirect: true })
        if (![200, 20000].includes(res.data.code)) throw new Error(res.data.message)
        const data = res.data.data
        this.rows = data.positions.map(row => ({ ...row, orderPrice: row.price > 0 ? Number(Number(row.price).toFixed(3)) : null }))
        this.stockPrices = {}
        this.groupRows(this.rows).forEach(stock => {
          const quote = stock.rows.find(row => Number(row.price) > 0)
          const price = quote ? Number(Number(quote.price).toFixed(3)) : null
          this.$set(this.stockPrices, stock.code, price)
          stock.rows.forEach(row => { row.orderPrice = price })
        })
        this.errors = data.errors
        this.snapshot = data.snapshot
        this.updatedAt = new Date(data.updated_at).toLocaleTimeString('zh-CN')
        this.consumed = false
      } catch (e) { this.error = e.response?.data?.detail || e.message || '持仓查询失败' }
      finally { this.loading = false }
    },
    confirmSell(ids) {
      const rows = this.rows.filter(row => ids.includes(row.id))
      if (!rows.length || rows.length > 50) { this.error = '请选择1至50笔持仓'; return }
      this.confirmationCodes = [...new Set(rows.map(row => row.code))]
      this.pendingIds = ids.slice()
      this.percent = 1
      this.submitError = ''
      this.confirming = true
    },
    async submit() {
      if (this.busy || this.consumed) return
      if (!this.pendingRows.length || this.pendingRows.length > 50 || this.pendingRows.some(row => !this.canSell(row) || this.sellQuantity(row) <= 0 || !Number.isFinite(Number(row.orderPrice)) || Number(row.orderPrice) <= 0)) {
        this.submitError = '请选择1至50个可卖账户，并在账户明细中填写有效委托价格'
        return
      }
      this.busy = true
      this.consumed = true
      try {
        const res = await request_zzquant({
          url: '/v3/quant/real/sell', method: 'post', timeout: 150000,
          data: { snapshot: this.snapshot, items: this.pendingRows.map(row => ({ id: row.id, price: Number(row.orderPrice), percent: this.percent })) }
        })
        if (![200, 20000].includes(res.data.code)) throw new Error(res.data.message)
        this.results = res.data.data
        this.confirming = false
      } catch (e) {
        this.submitError = e.response?.data?.detail || '请求结果未知，请核对交易端委托后刷新持仓'
        this.error = this.submitError
      } finally { this.busy = false }
    }
  }
}
</script>

<style scoped>
.real-positions { background: var(--bg-card, var(--p-card-bg)); color: var(--text-main, var(--p-text-main)); border: 1px solid var(--border-color, var(--p-border)); border-radius: 8px; }
.real-positions .table { color: inherit; }
.real-positions th, .real-positions td { border-color: var(--border-color, var(--p-border)); vertical-align: middle; white-space: nowrap; }
.real-positions .form-control, .real-positions .custom-select { background-color: var(--bg-card, var(--p-card-bg)); color: inherit; }
.group-select { max-width: 150px; }
.price-input { width: 112px; flex: 0 0 112px; }
.stock-group { border: 1px solid var(--p-border); border-radius: 6px; overflow: hidden; }
.stock-header { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; background: var(--p-pill-bg); }
.stock-title { color: inherit; background: transparent; border: 0; padding: 0; text-align: left; }
.holdings-toggle { display: flex; align-items: center; color: inherit; background: transparent; border: 0; padding: 0; text-align: left; }
.holdings-toggle:focus-visible, .stock-title:focus-visible { outline: 2px solid var(--p-text-main); }
.stock-summary { margin-right: auto; }
.account-table { font-size: 0.85rem; }
@media (max-width: 576px) { .stock-summary { flex-basis: 100%; padding-left: 28px; } }
@media (max-width: 576px) { .real-positions .group-select { margin-bottom: 8px; } .real-positions button { margin-bottom: 8px; } }
</style>
